/**
 * Resolve the environment label reported on Sentry events.
 *
 * Vite's `import.meta.env.MODE` is a *build* mode, not a deployment. Every
 * `npm run build` — prod, staging, or a one-off — produces MODE === 'production',
 * so using MODE alone made staging's frontend errors indistinguishable from
 * production's in Sentry.
 *
 * `VITE_APP_ENV` is the explicit override, baked in at build time by the
 * Dockerfile's ARG of the same name (mirroring how VITE_SENTRY_DSN is passed).
 * The staging Render service sets it to 'staging'; production may set it to
 * 'production' or leave it unset and fall back to MODE, which already says
 * 'production' there.
 *
 * This is a telemetry label, not a behavioural gate, so it passes values
 * through rather than enumerating them — an unrecognized label is honest and
 * harmless, whereas a wrong *behaviour* is not. The behavioural gates live in
 * backend `app/core/config.py` and enumerate their permitted values instead.
 */
export function resolveSentryEnvironment(
  env: { VITE_APP_ENV?: string; MODE?: string } = import.meta.env,
): string {
  const explicit = env.VITE_APP_ENV?.trim()
  if (explicit) return explicit
  return env.MODE ?? 'unknown'
}
